import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthCard from '../components/AuthCard';
import ErrorBox from '../components/ErrorBox';

export default function TwoFactor() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputs = useRef([]);

  useEffect(() => {
    if (!state?.token) navigate('/', { replace: true });
    inputs.current[0]?.focus();
  }, []);

  function handleChange(i, val) {
    const clean = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = clean;
    setDigits(next);
    if (clean && i < 5) inputs.current[i + 1]?.focus();
    if (next.every((d) => d !== '')) submitCode(next.join(''));
  }

  function handleKeyDown(i, e) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    inputs.current[Math.min(pasted.length, 5)]?.focus();
    if (pasted.length === 6) submitCode(pasted);
  }

  async function submitCode(code) {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('https://membify.dk/api/2fa/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${state.token}`,
        },
        body: JSON.stringify({ code }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        window.location.href = data.redirect || '/';
        return;
      }

      setDigits(['', '', '', '', '', '']);
      inputs.current[0]?.focus();

      if (res.status === 401) {
        setError({
          title: 'Forkert kode',
          message: 'Koden er forkert eller udløbet. Tjek din authenticator-app og prøv igen.',
          type: 'auth',
        });
      } else if (res.status === 429) {
        setError({
          title: 'For mange forsøg',
          message: 'Du har indtastet for mange forkerte koder. Vent et øjeblik og prøv igen.',
          type: 'ratelimit',
          support: true,
        });
      } else {
        setError({
          title: 'Noget gik galt',
          message: 'Vi kunne ikke verificere koden. Prøv igen eller kontakt support.',
          type: 'unknown',
          support: true,
        });
      }
    } catch {
      setError({
        title: 'Ingen forbindelse',
        message: 'Tjek din internetforbindelse og prøv igen.',
        type: 'network',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthCard backTo="/">
      <div className="page-header">
        <div className="page-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        </div>
        <h1 className="page-title">To-faktor godkendelse</h1>
        <p className="page-desc">
          Indtast den 6-cifrede kode fra din authenticator-app for{' '}
          <strong>{state?.email}</strong>
        </p>
      </div>

      {error && <ErrorBox error={error} />}

      <div className="otp-row" onPaste={handlePaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (inputs.current[i] = el)}
            className={`otp-input${loading ? ' otp-loading' : ''}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={loading}
            autoComplete="one-time-code"
          />
        ))}
      </div>

      {loading && (
        <div className="otp-verifying">
          <span className="btn-spinner btn-spinner--dark" />
          Verificerer...
        </div>
      )}

      <p className="otp-hint">
        Du kan også indsætte koden direkte — den udfyldes automatisk.
      </p>

      <p className="login-footer" style={{ marginTop: '20px' }}>
        Mistet adgang til din authenticator?{' '}
        <a href="mailto:support@membify.dk" className="link-btn">Kontakt support</a>
      </p>
    </AuthCard>
  );
}
